import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const DEFAULT_PROJECT = "PRJ-brightspeed";

// Session state lives in an httpOnly cookie managed by the backend - no
// token is ever written to localStorage/sessionStorage (XSS hardening).
// Only the non-sensitive active project id is persisted client-side.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [projectId, setProjectIdState] = useState(() => localStorage.getItem("tc_project_id") || "");
  const [booting, setBooting] = useState(true);

  const allProjects = workspaces.flatMap((w) => (w.projects || []).map((p) => ({ ...p, workspace: w })));
  const currentProject = allProjects.find((p) => p.id === projectId) || null;
  const currentWorkspace = currentProject?.workspace || null;

  const loadContext = useCallback(async () => {
    const res = await api.get("/center/context");
    const ws = res.data.workspaces || [];
    setUser(res.data.user);
    setWorkspaces(ws);
    const projects = ws.flatMap((w) => w.projects || []);
    const stored = localStorage.getItem("tc_project_id");
    let pid = projects.some((p) => p.id === stored) ? stored : "";
    if (!pid) {
      pid = projects.some((p) => p.id === DEFAULT_PROJECT) ? DEFAULT_PROJECT : (projects[0]?.id || "");
    }
    if (pid) {
      localStorage.setItem("tc_project_id", pid);
      setProjectIdState(pid);
    }
    return res.data;
  }, []);

  // Boot: the httpOnly cookie (if present) authenticates this call.
  useEffect(() => {
    loadContext().catch(() => {}).finally(() => setBooting(false));
  }, [loadContext]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* cookie may already be gone */ }
    localStorage.removeItem("tc_project_id");
    setUser(null);
    setWorkspaces([]);
    window.location.href = "/login";
  }, []);

  const switchProject = useCallback((pid) => {
    localStorage.setItem("tc_project_id", pid);
    setProjectIdState(pid);
    // Full refresh so every workspace page re-fetches under the new scope
    window.location.href = "/overview";
  }, []);

  return (
    <AuthContext.Provider value={{
      user, booting, workspaces, allProjects,
      projectId, currentProject, currentWorkspace,
      login, logout, switchProject, loadContext,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
